"use client";
import React, { useCallback, useMemo, useRef, useState } from "react";
import Image from "next/image";
import dayjs from "@/utils/dayjs";
import { MOMENT_FORMAT } from "@/constants";
import { useSubscription } from "@/providers/WebSocketProvider";
import { useTranslation } from "react-i18next";
import { WeatherForecastData } from "@/types/forecast";
import { ACTIVE_DAY, PEAK_TIME } from "@/app/(pages)/Home/constants/shared";
import {
  getDetailedIconName,
  getWeatherIcon,
  getWeatherIconName,
  weatherIconCorrection,
} from "@/utils/getWeatherIcon";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AllowGeolocationPanel from "@/app/(components)/AllowGeolocationPanel";
import WeatherPrecipitation
  from "@/app/(pages)/Home/components/mobile/MainWeatherDisplay/components/WeatherPrecipitation";
import cx from "clsx";
import { getPrecipitation } from "@/app/(pages)/Home/components/mobile/MainWeatherDisplay/features/getPrecipitation";
import HeroStrip, { Model, } from "@/app/(pages)/Home/components/mobile/HeroStrip";
import {
  getDayDisplayCode,
  getSegmentCodeAtHour,
} from "@/app/(pages)/Home/components/shared/Highcharts/components/WeatherTimeline";
import { getCityClockFromDay } from "@/utils/cityTime";
import { isNightForDay } from "@/utils/dayNight";

type MainInfoProps = {
  forecast: WeatherForecastData;
  city: string;
  currentDay: string;
  isFirstEnter: boolean;
};

dayjs.extend(customParseFormat);

const DIR_KEYS = [
  "north",
  "north_east",
  "east",
  "south_east",
  "south",
  "south_west",
  "west",
  "north_west",
] as const;

const dirKey = (deg: number) => DIR_KEYS[Math.round(deg / 45) % 8];

const MainInfo = ({
  forecast,
  city,
  currentDay,
  isFirstEnter,
}: MainInfoProps) => {
  const [activeDay, setActiveDay] = useState(currentDay);
  const [model, setModel] = useState<Model>(null);
  const day = forecast[activeDay] ?? forecast[currentDay];
  const { hour: cityHour, todayKey } = getCityClockFromDay(day);
  const { t, i18n } = useTranslation();
  const cityPanelRef = useRef(null);
  const cloudRef = useRef<HTMLImageElement>(null);

  const { formattedDate, dayOfWeek, dateTime, isCurrentDay } = useMemo(() => {
    const formattedDate = dayjs(day.date, `YYYY-MM-DD`)
      .locale(i18n.language)
      .format("DD MMMM");

    const dateTime = dayjs(day.date, MOMENT_FORMAT).toISOString();

    const dayOfWeek = dayjs(day.date, MOMENT_FORMAT)
      .locale(i18n.language)
      .format("dddd");

    const dayStr = dayjs(day.date, MOMENT_FORMAT).format(MOMENT_FORMAT);
    const isCurrentDay = dayStr === todayKey;

    return { formattedDate, dateTime, dayOfWeek, isCurrentDay };
  }, [day.date, i18n.language, todayKey]);

  useSubscription(ACTIVE_DAY, (value) => {
    setActiveDay(value);
  });

  const cb = useCallback(
    (m) => {
      setModel(m);
    },
    [activeDay],
  );

  const currentHour = cityHour;
  const hourIndex = isCurrentDay ? currentHour : PEAK_TIME;
  const isNight = isCurrentDay && isNightForDay(day);
  const weatherCode = isCurrentDay
    ? (getSegmentCodeAtHour(day, currentHour) ?? day.weatherCode)
    : (getDayDisplayCode(day) ?? day.weatherCode);
  const precip = getPrecipitation(weatherIconCorrection(weatherCode).code);
  const weatherIcon = getWeatherIcon(weatherCode);
  const { sun } = getDetailedIconName(weatherCode, isNight);

  const bigTemp = Math.floor(
    isCurrentDay
      ? (day.temperature?.[currentHour] ?? day.maxTemperature)
      : day.maxTemperature,
  );
  const feels = Math.floor(day.feelsLike[hourIndex]);
  const tempDiff = day.vsYesterday?.temperature;
  const windSpeed = Math.floor(day.windSpeed?.[hourIndex] ?? 0);
  const windDir = Math.floor(day.windDirection?.[hourIndex] ?? 0);
  const precipSum = Math.round((day.precipitationSum ?? 0) * 10) / 10;

  return (
    <div className="flex h-full w-full gap-3 backdrop-blur-[2px]">
      <div className="relative z-30 flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-3">
          <div ref={cityPanelRef} className="relative">
            <div className="relative z-10 flex h-10 items-center gap-2 rounded-full border border-[rgba(179,151,253,.26)] bg-[rgba(10,8,29,.44)] px-4">
              <Image src="/shared/point.svg" alt="" width={18} height={18} />
              <h1 className="max-w-56 truncate overflow-hidden text-[15px] font-semibold text-nowrap">
                {city}
              </h1>
            </div>
          </div>
          <p className="text-[13px] text-white/45 first-letter:uppercase">
            <span className="first-letter:uppercase">{dayOfWeek}</span>,{" "}
            <time dateTime={dateTime}>{formattedDate}</time>
          </p>
          <AllowGeolocationPanel
            isFirstEnter={isFirstEnter}
            ref={cityPanelRef}
          />
        </div>

        <div className="relative mt-4 flex items-start leading-none">
          <div
            aria-hidden
            className="bg-neon-violet/20 pointer-events-none absolute top-0 left-6 h-32 w-32 rounded-full blur-[60px]"
          />
          <span
            className="bg-gradient-to-b from-white from-30% to-[oklch(0.742_0.1459_295.25)] bg-clip-text text-[clamp(56px,8vw,96px)] font-extrabold tracking-[-4px] text-transparent"
            style={{
              filter: "drop-shadow(0 6px 30px rgba(139,92,246,.35))",
            }}
          >
            {bigTemp}°
          </span>
          <span className="mt-3 text-3xl font-bold text-white/60">C</span>
        </div>
        <div className="relative mb-auto">
          <div
            aria-hidden
            className="bg-vivid-blue/40 pointer-events-none absolute bottom-0 left-0 -z-50 h-1/4 w-2/4 -translate-y-full rounded-full blur-[60px]"
          />

          <p className="mt-3 text-lg font-semibold">
            {t(`weather.${weatherCode}.description`)}
            <span className="font-normal text-white/60">
              {" "}
              · {t("metrics.feels_like")} {feels}°
            </span>
          </p>

          <p className="mt-1.5 text-sm font-medium text-white/60">
            {t("hero.max")} {Math.floor(day.maxTemperature)}° · {t("hero.min")}{" "}
            {Math.floor(day.minTemperature)}°
            {precipSum > 0 && (
              <>
                {" "}
                &nbsp;·&nbsp; {t("hero.precip")}: {precipSum}{" "}
                {t("metrics.precipitation_sum_metric", { defaultValue: "мм" })}
              </>
            )}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {model?.chip && (
              <span
                className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[13px] ${
                  model.chipTone === "cold"
                    ? "border-[#5ab4f0]/40 bg-[#5ab4f0]/8 text-[#7cc5f5]"
                    : model.chipTone === "warm"
                      ? "border-[#f5a34f]/45 bg-[#f5a34f]/8 text-[#f7b56b]"
                      : "border-white/25 bg-white/8"
                }`}
              >
                {model.chip}
              </span>
            )}
            {windSpeed > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(102,207,227,.3)] bg-[rgba(102,207,227,.08)] px-3 py-1.5 text-xs font-semibold text-[#a5e6f2]">
                {t("hero.wind_badge", {
                  value: windSpeed,
                  dir: t(`panel.wind_direction.${dirKey(windDir)}`),
                })}
              </span>
            )}
          </div>
        </div>
        <HeroStrip
          activeDay={activeDay}
          weather={forecast}
          isMobile={false}
          callback={cb}
        />
        <div
          aria-hidden
          className="bg-vivid-blue/20 pointer-events-none absolute -bottom-6 -left-6 -z-50 h-1/8 w-2/4 rounded-full blur-[60px]"
        />
      </div>

      <div
        className="pointer-events-none -z-30 hidden min-w-[260px] flex-1 md:block"
        aria-hidden
      >
        <Image
          src="/shared/house.webp"
          alt=""
          width={470}
          height={256}
          className="absolute right-0 -bottom-6 z-20 h-auto w-[min(96%,470px)] duration-300"
          style={{ filter: "drop-shadow(0 30px 44px rgba(5,3,20,.6))" }}
          loading="lazy"
        />
        <WeatherPrecipitation
          precip={precip}
          emitterRef={cloudRef}
          cone_spread={0.2}
          cone_lean={0.25}
          cone_exp={1}
          fullscreenFlash={false}
          fogHeightFactor={2}
          className={cx(
            "!-inset-6 z-30 !h-[calc(100%+3rem)] !w-[calc(100%+3rem)]",
            weatherCode === 45 ? "" : "",
          )}
        />
        <Image
          ref={cloudRef}
          src={`/weather/${getWeatherIconName(weatherCode, isNight)}.png`}
          alt={t(`weather.${weatherCode}.description`)}
          width={220}
          height={220}
          className={cx(
            "v-floaty absolute top-0 right-0 z-30 h-auto",
            weatherCode === 0 ? (isNight ? "w-40" : "icon-sun w-40") : "w-52",
          )}
          style={{ filter: "drop-shadow(0 18px 34px rgba(5,3,20,.5))" }}
        />
        <Image
          src={`/weather/${isNight ? "0.night" : "0"}.png`}
          alt=""
          aria-hidden
          width="120"
          height="120"
          loading="lazy"
          className={cx(
            "v-floaty-slow absolute -top-8 right-0 z-20 h-auto w-28 !-rotate-5",
            !sun && "hidden",
          )}
        />
        {weatherCode === 3 && (
          <Image
            src={`/weather/${weatherIcon}.png`}
            alt=""
            aria-hidden
            width="150"
            height="130"
            loading="lazy"
            className="v-floaty-slow absolute top-0 right-0 z-10 h-auto w-32 -translate-x-16/12 translate-y-full opacity-85 blur-[1.5px] brightness-90"
          />
        )}
      </div>
    </div>
  );
};

export default MainInfo;
